import React, { useState, useEffect } from 'react'
import { IoArrowBack, IoFootball, IoSquare, IoSwapHorizontal, IoLocationOutline, IoPersonOutline, IoCalendarOutline, IoChevronForward } from 'react-icons/io5'
import type { FixtureLite, FixtureBuilderOut, TeamRollupOut, TeamSeasonStatsOut, HeadToHeadOut, FixturePlayersOut, H2HStatsResponse, H2HFilter, CardsContextResponse, TeamLite } from '../types'
import { fmtKickoff } from '../utils'
import { TabNav, BuilderTab, TeamsTab, PlayersTab, H2HTab, CardsTab, InsightsSummary, FixtureNotes, PlayerProfileModal } from './index'
import { MATableTab } from './match-analysis/tabs/MATableTab'
import type { Tab } from './TabNav'

// Placeholder for missing team crests
const TeamPlaceholder = ({ name }: { name: string }) => (
  <div className="match-hero-logo-placeholder">
    {name.charAt(0).toUpperCase()}
  </div>
)

// Countdown timer hook
function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date()
      const diff = targetDate.getTime() - now.getTime()

      if (diff <= 0) {
        setTimeLeft('')
        return
      }

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
    }

    calculateTimeLeft()
    const interval = setInterval(calculateTimeLeft, 1000)
    return () => clearInterval(interval)
  }, [targetDate])

  return timeLeft
}

interface MatchPageProps {
  fixture: FixtureLite
  builder: FixtureBuilderOut | null
  teams: TeamRollupOut[] | null
  seasonStats: TeamSeasonStatsOut[] | null
  headToHead: HeadToHeadOut | null
  players: FixturePlayersOut | null
  h2hStats: H2HStatsResponse | null
  h2hFilter: H2HFilter
  onH2HFilterChange: (filter: H2HFilter) => void
  cardsContext: CardsContextResponse | null
  onBack: () => void
  onRefereeClick?: (refereeId: number) => void
  onTeamClick?: (team: TeamLite) => void
}

/**
 * Full-page match detail view (FotMob style)
 */
export function MatchPage({
  fixture,
  builder,
  teams,
  seasonStats,
  headToHead,
  players,
  h2hStats,
  h2hFilter,
  onH2HFilterChange,
  cardsContext,
  onBack,
  onRefereeClick,
  onTeamClick,
}: MatchPageProps) {
  const [activeTab, setActiveTab] = useState<Tab>('builder')
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null)

  const isLive = fixture.status === 'LIVE'
  const isFinished = fixture.status === 'FT'
  const hasStarted = isLive || isFinished

  const fixtureLabel = `${fixture.home_team.name} vs ${fixture.away_team.name}`

  // Get referee - prefer from fixture directly, fallback to builder
  const refereeName = fixture.referee_name || builder?.referee?.name
  // Get referee ID from cardsContext (most reliable source)
  const refereeId = cardsContext?.referee?.id

  // Format kickoff date and time
  const kickoffDate = new Date(fixture.kickoff_utc)
  const formattedDateTime = kickoffDate.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  }) + ', ' + fmtKickoff(fixture.kickoff_utc)

  // Countdown timer for upcoming matches
  const countdown = useCountdown(kickoffDate)

  // Round/competition stage display
  const roundDisplay = fixture.round || (fixture.league_name ? `${fixture.league_name}` : '')

  return (
    <div className="match-page">
      {/* Header Bar - Back button, League/Round, Follow */}
      <div className="match-page-header">
        <div className="match-page-header-left">
          <button className="match-page-back" onClick={onBack} aria-label="Go back">
            <IoArrowBack size={20} />
          </button>
          <span className="match-page-back-label">Matches</span>
        </div>
        <div className="match-page-header-center">
          <span className="match-page-competition">{roundDisplay}</span>
        </div>
        <div className="match-page-header-right">
          {/* Follow button placeholder */}
        </div>
      </div>

      {/* Info Bar - Date, Venue, Referee */}
      <div className="match-info-bar">
        <div className="match-info-item">
          <IoCalendarOutline size={14} />
          <span>{formattedDateTime}</span>
        </div>
        {fixture.venue && (
          <div className="match-info-item">
            <IoLocationOutline size={14} />
            <span>{fixture.venue}</span>
          </div>
        )}
        {refereeName && (
          <button
            type="button"
            className="match-info-item referee"
            onClick={() => refereeId && onRefereeClick?.(refereeId)}
            disabled={!refereeId}
          >
            <IoPersonOutline size={14} />
            <span>{refereeName}</span>
            <IoChevronForward size={12} className="referee-arrow" />
          </button>
        )}
      </div>

      {/* Match Hero - Teams and Score/Time */}
      <div className="match-hero">
        {/* Home Team */}
        <button
          type="button"
          className="match-hero-team home clickable"
          onClick={() => onTeamClick?.(fixture.home_team)}
        >
          <span className="match-hero-name">{fixture.home_team.name}</span>
          {fixture.home_team.crest_url ? (
            <img
              src={fixture.home_team.crest_url}
              alt={fixture.home_team.name}
              className="match-hero-logo"
            />
          ) : (
            <TeamPlaceholder name={fixture.home_team.name} />
          )}
        </button>

        {/* Score / Time Center */}
        <div className="match-hero-center">
          {hasStarted ? (
            <>
              <div className="match-hero-score">
                <span className="match-hero-score-home">{fixture.home_score ?? 0}</span>
                <span className="match-hero-score-separator">-</span>
                <span className="match-hero-score-away">{fixture.away_score ?? 0}</span>
              </div>
              <div className={`match-hero-status ${isLive ? 'live' : ''}`}>
                {isLive ? (
                  <>
                    <span className="live-dot" />
                    {fixture.elapsed ? `${fixture.elapsed}'` : 'LIVE'}
                  </>
                ) : (
                  'FT'
                )}
              </div>
            </>
          ) : (
            <>
              <div className="match-hero-time">{fmtKickoff(fixture.kickoff_utc)}</div>
              {countdown && <div className="match-hero-countdown">{countdown}</div>}
            </>
          )}
        </div>

        {/* Away Team */}
        <button
          type="button"
          className="match-hero-team away clickable"
          onClick={() => onTeamClick?.(fixture.away_team)}
        >
          {fixture.away_team.crest_url ? (
            <img
              src={fixture.away_team.crest_url}
              alt={fixture.away_team.name}
              className="match-hero-logo"
            />
          ) : (
            <TeamPlaceholder name={fixture.away_team.name} />
          )}
          <span className="match-hero-name">{fixture.away_team.name}</span>
        </button>
      </div>

      {/* Match Events Timeline (if match has started) */}
      {hasStarted && fixture.events && fixture.events.length > 0 && (() => {
        // Deduplicate events that were ingested multiple times during live updates
        // Same player + same event_type + same detail + elapsed within 2 mins = duplicate
        const seen = new Map<string, typeof fixture.events[0]>()
        for (const event of fixture.events) {
          const baseKey = `${event.player_name}-${event.event_type}-${event.detail}`
          const existing = seen.get(baseKey)

          if (!existing) {
            seen.set(baseKey, event)
          } else {
            const timeDiff = Math.abs(event.elapsed - existing.elapsed)
            if (timeDiff <= 2) {
              // Keep the later one (more accurate timing)
              if (event.elapsed >= existing.elapsed) {
                seen.set(baseKey, event)
              }
            } else {
              // Different time = separate events (e.g., two actual yellows for same player)
              const uniqueKey = `${baseKey}-${Math.floor(event.elapsed / 10)}`
              seen.set(uniqueKey, event)
            }
          }
        }
        const dedupedEvents = Array.from(seen.values())

        // For red cards, determine if it's a second yellow or straight red
        const getRedCardType = (event: typeof dedupedEvents[0]): 'second_yellow' | 'straight' => {
          if (!event.player_name) return 'straight'
          // If player has a yellow card before this red, it's a second yellow
          const hasYellowBefore = dedupedEvents.some(
            e => e.event_type === 'Card' &&
                 e.detail === 'Yellow Card' &&
                 e.player_name === event.player_name &&
                 e.elapsed < event.elapsed
          )
          return hasYellowBefore ? 'second_yellow' : 'straight'
        }

        // Sort by elapsed time
        const sortedEvents = [...dedupedEvents].sort((a, b) => {
          const aTime = a.elapsed + (a.elapsed_extra || 0) / 100
          const bTime = b.elapsed + (b.elapsed_extra || 0) / 100
          return aTime - bTime
        })

        return (
          <div className="match-events-timeline">
            {sortedEvents.map((event, idx) => (
              <div
                key={idx}
                className={`match-event ${event.team_id === fixture.home_team.id ? 'home' : 'away'}`}
              >
                <div className="match-event-minute">
                  {event.elapsed}'
                  {event.elapsed_extra && event.elapsed_extra > 0 && `+${event.elapsed_extra}`}
                </div>
                <div className="match-event-icon">
                  {event.event_type === 'Goal' && <IoFootball size={16} className="goal-icon" />}
                  {event.event_type === 'Card' && event.detail === 'Yellow Card' && (
                    <IoSquare size={14} className="yellow-card-icon" />
                  )}
                  {event.event_type === 'Card' && event.detail === 'Red Card' && (
                    <IoSquare size={14} className="red-card-icon" />
                  )}
                  {event.event_type === 'subst' && <IoSwapHorizontal size={16} className="sub-icon" />}
                  {event.event_type === 'Var' && <span className="var-icon">VAR</span>}
                </div>
                <div className="match-event-details">
                  {event.player_id ? (
                    <span
                      className="match-event-player clickable"
                      onClick={() => setSelectedPlayerId(event.player_id!)}
                    >
                      {event.player_name}
                    </span>
                  ) : (
                    <span className="match-event-player">{event.player_name}</span>
                  )}
                  {event.event_type === 'Card' && event.detail === 'Red Card' && (
                    <span className="match-event-card-type">
                      ({getRedCardType(event) === 'second_yellow' ? '2nd yellow' : 'straight red'})
                    </span>
                  )}
                  {event.assist_player_name && (
                    event.assist_player_id ? (
                      <span
                        className="match-event-assist clickable"
                        onClick={() => setSelectedPlayerId(event.assist_player_id!)}
                      >
                        ({event.assist_player_name})
                      </span>
                    ) : (
                      <span className="match-event-assist">({event.assist_player_name})</span>
                    )
                  )}
                  {event.event_type === 'Var' && event.comments && (
                    <span className="match-event-var-detail">{event.comments}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      })()}

      {/* Tab Navigation */}
      <div className="match-tabs">
        <TabNav activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Tab Content */}
      <div className="match-content">
        {activeTab === 'builder' && (
          <BuilderTab
            builder={builder}
            fixtureId={fixture.id}
            fixtureLabel={fixtureLabel}
            homeTeamId={fixture.home_team.id}
            awayTeamId={fixture.away_team.id}
          />
        )}
        {activeTab === 'teams' && (
          <TeamsTab
            teams={teams}
            seasonStats={seasonStats}
            headToHead={headToHead}
            fixtureId={fixture.id}
          />
        )}
        {activeTab === 'players' && <PlayersTab players={players} />}
        {activeTab === 'h2h' && (
          <H2HTab
            stats={h2hStats}
            teams={teams}
            filter={h2hFilter}
            onFilterChange={onH2HFilterChange}
          />
        )}
        {activeTab === 'cards' && <CardsTab teams={teams} cardsContext={cardsContext} />}
        {activeTab === 'table' && fixture.league_id && (
          <MATableTab
            fixtureId={fixture.id}
            homeTeamId={fixture.home_team.id}
            awayTeamId={fixture.away_team.id}
            leagueId={fixture.league_id}
            leagueName={fixture.league_name ?? undefined}
          />
        )}
        {activeTab === 'ai' && <InsightsSummary fixtureId={fixture.id} />}

        {/* Fixture Notes */}
        <FixtureNotes fixtureId={fixture.id} />
      </div>

      {/* Player Profile Modal */}
      {selectedPlayerId && (
        <PlayerProfileModal
          playerId={selectedPlayerId}
          onClose={() => setSelectedPlayerId(null)}
        />
      )}
    </div>
  )
}
